import {
  CareerSiteFiltersOption,
  CategoryOptions,
  RadiusOptions,
} from 'api-clients/monolith';
import { createContext, Dispatch, SetStateAction } from 'react';
import { IntlShape } from 'react-intl';

import { allFilterOption, anyFilterOption } from 'utils/filter-options';

export const getSortOptions = (intl: IntlShape) => [
  {
    key: 'distance',
    label: intl.formatMessage({
      defaultMessage: 'Distance',
      description: 'Sort by distance option',
    }),
  },
  {
    key: 'title',
    label: intl.formatMessage({
      defaultMessage: 'Opening Name',
      description: 'Sort by opening name option',
    }),
  },
  {
    key: 'date_posted',
    label: intl.formatMessage({
      defaultMessage: 'Date Posted',
      description: 'Sort by date posted option',
    }),
  },
];

export type SortFilter = ReturnType<typeof getSortOptions>[number];

export interface ActiveFilters {
  compensationType: CareerSiteFiltersOption;
  sortBy: SortFilter;
  radius: RadiusOptions;
  category: CategoryOptions;
  department: CareerSiteFiltersOption;
  location: CareerSiteFiltersOption;
  position: CareerSiteFiltersOption;
}

export interface Segment {
  id: string | null;
  value: string | null;
}

export type AppContextData = {
  accountSlug: string | undefined;
  brandId: string | undefined;
  brandName: string;
  activeFilters: ActiveFilters;
  setActiveFilters: Dispatch<SetStateAction<ActiveFilters>>;
  location: Segment;
  setLocation: Dispatch<SetStateAction<Segment>>;
  position: Segment;
  setPosition: Dispatch<SetStateAction<Segment>>;
  categoryName: string | null;
  setCategoryName: Dispatch<SetStateAction<string | null>>;
  experienceLevelName: string | null;
  setExperienceLevelName: Dispatch<SetStateAction<string | null>>;
  setBrandDetails: (
    accountSlug: string,
    brandId: string | undefined,
    brandName: string,
  ) => void;
  placeId: string | null;
  setPlaceId: Dispatch<SetStateAction<string | null>>;
  jobSearchQuery: string | null;
  setJobSearchQuery: Dispatch<SetStateAction<string | null>>;
  isFetchingJobs: boolean;
  setIsFetchingJobs: Dispatch<SetStateAction<boolean>>;
  resetFilters: () => void;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  isMobile: boolean;
  isJobsFromCurrentLocation: boolean;
  setIsJobsFromCurrentLocation: Dispatch<SetStateAction<boolean>>;
  setDefaultFilterData: (data: ActiveFilters) => void;
  updateUrlWithParams: (
    accountSlug: string,
    brandId: string | undefined,
    key: string,
    value: string | number,
  ) => void;
  resetUrlParams: (accountSlug: string, brandId: string) => void;
  currentLanguage: string;
  setCurrentLanguage: Dispatch<SetStateAction<string>>;
};

export const getDefaultActiveFilters = (intl: IntlShape): ActiveFilters => ({
  compensationType: anyFilterOption(intl),
  sortBy: getSortOptions(intl)[0],
  radius: anyFilterOption(intl),
  category: anyFilterOption(intl),
  department: allFilterOption(intl),
  location: allFilterOption(intl),
  position: allFilterOption(intl),
});

export const defaultAppContext: AppContextData = {
  accountSlug: undefined,
  brandId: undefined,
  brandName: 'abc',
  activeFilters: {
    compensationType: { key: 'any', label: 'Any' },
    sortBy: { key: 'distance', label: 'Distance' },
    radius: { key: 'Any', label: 'Any' },
    category: { key: 'Any', label: 'Any' },
    department: { key: 'all', label: 'all' },
    location: { key: 'all', label: 'all' },
    position: { key: 'all', label: 'all' },
  },
  setActiveFilters: () => undefined,
  location: { id: null, value: null },
  setLocation: () => undefined,
  position: { id: null, value: null },
  setPosition: () => undefined,
  categoryName: null,
  setCategoryName: () => undefined,
  experienceLevelName: null,
  setExperienceLevelName: () => undefined,
  setBrandDetails: () => undefined,
  placeId: null,
  setPlaceId: () => undefined,
  jobSearchQuery: null,
  setJobSearchQuery: () => undefined,
  isFetchingJobs: false,
  setIsFetchingJobs: () => undefined,
  resetFilters: () => undefined,
  page: 1,
  setPage: () => undefined,
  isMobile: false,
  isJobsFromCurrentLocation: true,
  setIsJobsFromCurrentLocation: () => undefined,
  setDefaultFilterData: () => undefined,
  updateUrlWithParams: () => undefined,
  resetUrlParams: () => undefined,
  currentLanguage: 'en',
  setCurrentLanguage: () => undefined,
};

export const AppContext = createContext<AppContextData>(defaultAppContext);
